// WarRoom — T062, T064, T066 — full-screen display-only dashboard
import { useState, useEffect, useCallback } from 'react';
import StatusBadge from '../components/StatusBadge';
import { getMonitors, type Monitor } from '../services/api';
import { useAppSettings } from '../hooks/useAppSettings';

const AUTO_REFRESH_MS = 30_000;

export default function WarRoom() {
  const { appTitle } = useAppSettings();
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchMonitors = useCallback(async () => {
    try {
      const result = await getMonitors({});
      setMonitors(result.data || []);
      setLastUpdated(new Date());
    } catch {
      // silently retry on next interval
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonitors();
    const interval = setInterval(fetchMonitors, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchMonitors]);

  const upCount = monitors.filter((m) => m.state === 'UP').length;
  const degradedCount = monitors.filter((m) => m.state === 'DEGRADED').length;
  const downCount = monitors.filter((m) => m.state === 'DOWN').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="animate-pulse text-text-secondary text-2xl">Loading War Room...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-white text-3xl font-bold">{appTitle} — War Room</h1>
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            <StatusBadge state="UP" size="lg" />
            <span className="text-text-primary text-3xl font-bold">{upCount}</span>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge state="DEGRADED" size="lg" />
            <span className="text-text-primary text-3xl font-bold">{degradedCount}</span>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge state="DOWN" size="lg" />
            <span className="text-text-primary text-3xl font-bold">{downCount}</span>
          </div>
        </div>
      </div>

      {/* Monitor grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
        {monitors.map((m) => (
          <div
            key={m.id}
            className={`rounded-lg p-4 border ${
              m.state === 'DOWN' ? 'bg-status-down/20 border-status-down' :
              m.state === 'DEGRADED' ? 'bg-status-degraded/20 border-status-degraded' :
              'bg-surface border-border'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <StatusBadge state={m.state} size="sm" />
              {m.muted && <span className="text-text-muted text-xs">🔇</span>}
            </div>
            <h3 className="text-text-primary font-semibold text-sm truncate">{m.name}</h3>
            <p className="text-text-muted text-xs truncate mt-1">{m.url}</p>
            {m.knownIPs && m.knownIPs.length > 0 && (
              <p className="text-text-muted text-xs mt-1">IP: {m.knownIPs.join(', ')}</p>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <p className="text-text-muted text-xs mt-8 text-center">
        Auto-refreshes every 30s · Last updated: {lastUpdated.toLocaleTimeString()}
      </p>
    </div>
  );
}
